/**
 * Mapbox lookups the Portal composer's sheets use: places near a city (Search Box forward,
 * strong POI coverage, biased to the city and kept within 80km of it) and cities by name
 * (Geocoding places/localities). Both answer [] without a token or on any failure — a sheet
 * with no rows, never a broken sheet.
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAX_KM_FROM_CITY = 80;

export function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function slugify(text) {
  return (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

/** [{ name, address, lat, lng }] near `city` ({ lat, lng }). */
export async function searchPlaces(q, city, { fetchFn = fetch, token = MAPBOX_TOKEN, unbounded = false } = {}) {
  const query = (q || '').trim();
  if (!query || !token) return [];
  try {
    const params = new URLSearchParams({ q: query, limit: '10', language: 'en', types: 'poi,address,place', access_token: token });
    if (city?.lng != null && city?.lat != null) params.set('proximity', `${city.lng},${city.lat}`);
    const data = await (await fetchFn(`https://api.mapbox.com/search/searchbox/v1/forward?${params.toString()}`)).json();
    let items = (data.features || []).map((f) => {
      const [lng, lat] = f.geometry?.coordinates || [];
      const p = f.properties || {};
      // The town it's in, for a match outside the chosen city ("KELOWNA · 54 KM AWAY").
      const town = p.context?.place?.name || p.context?.locality?.name || '';
      return { name: p.name || p.name_preferred || '', address: p.full_address || p.address || p.place_formatted || '', lat, lng, town };
    }).filter((it) => it.name && typeof it.lat === 'number');
    // `unbounded`: the wider search when nothing matched in the chosen city — keep every hit.
    if (!unbounded && city?.lat != null && city?.lng != null) items = items.filter((it) => distanceKm(city.lat, city.lng, it.lat, it.lng) <= MAX_KM_FROM_CITY);
    return items;
  } catch { return []; }
}

/** [{ slug, label, lat, lng }] for a city name. */
export async function searchCities(q, { fetchFn = fetch, token = MAPBOX_TOKEN } = {}) {
  const query = (q || '').trim();
  if (!query || !token) return [];
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?types=place,locality&limit=6&language=en&access_token=${token}`;
    const data = await (await fetchFn(url)).json();
    return (data.features || []).map((f) => {
      const text = f.text || '';
      const ctx = f.context || [];
      const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
      const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
      const [lng, lat] = f.center || [];
      return { slug: slugify([text, region || country].filter(Boolean).join('-')), label: [text, region, country].filter(Boolean).join(', '), lat, lng };
    }).filter((r) => r.slug);
  } catch { return []; }
}
